import {
  CopilotResponseActionType,
  HealtProcessSummaryResponseParameters,
  ThermalLiDARResponseParameters,
  LaserScanParameterModel,
  MikeMessageParameters,
} from "../../../../../../../types/copilot";

/**
 * Generic conversation ID used for default/generic conversations
 */
export const GENERIC_CONVERSATION_ID = "11111111-0000-0000-0000-000000000000";

/**
 * Helper function to parse and type cast mike message parameters based on action type
 * @param actionType - The response action type
 * @param parameters - The raw parameters string or object
 * @returns Typed parameters object
 */
export function parseMikeMessageParameters<T extends MikeMessageParameters>(
  actionType: CopilotResponseActionType,
  parameters?: string | MikeMessageParameters,
): T {
  let parsedParams: MikeMessageParameters = {};

  // Parse parameters if it's a string
  if (typeof parameters === "string") {
    try {
      parsedParams = JSON.parse(parameters || "{}");
    } catch (error) {
      parsedParams = {};
    }
  } else if (parameters) {
    parsedParams = parameters;
  }

  // Return typed parameters based on action type
  switch (actionType) {
    case CopilotResponseActionType.HealtProcessSummary:
      return parsedParams as T;

    case CopilotResponseActionType.ThermalScan:
      return parsedParams as T;

    case CopilotResponseActionType.LaserScan:
      return parsedParams as T;

    default:
      return parsedParams as T;
  }
}

/**
 * @param actionType - The response action type
 * @param parameters - The raw parameters string or object
 * @returns Campaign Heat ID if found
 */
export function getCampaignHeatId(
  actionType: CopilotResponseActionType,
  parameters?: string | MikeMessageParameters,
): string | undefined {
  const parsedParams = parseMikeMessageParameters(actionType, parameters);

  switch (actionType) {
    case CopilotResponseActionType.HealtProcessSummary: {
      const healtParams = parsedParams as HealtProcessSummaryResponseParameters;
      return healtParams.CampaignHeat?.CampaignHeatId;
    }

    case CopilotResponseActionType.ThermalScan: {
      const thermalParams = parsedParams as ThermalLiDARResponseParameters;
      return (
        thermalParams.ProcessedHeatThermalData?.CampaignHeatId ||
        thermalParams.LiDARData?.CampaignHeatId
      );
    }

    case CopilotResponseActionType.LaserScan: {
      const laserParams = parsedParams as ThermalLiDARResponseParameters;
      return (
        laserParams.LiDARData?.CampaignHeatId ||
        laserParams.ProcessedHeatThermalData?.CampaignHeatId
      );
    }

    default:
      return "";
  }
}

/**
 * Type guards for specific parameter types
 */
export function isHealtProcessSummaryParameters(
  params: MikeMessageParameters,
): params is HealtProcessSummaryResponseParameters {
  return (
    "CampaignHeat" in params ||
    "MinCourseClockThicknessAlgorithmResponse" in params
  );
}

export function isThermalLiDARParameters(
  params: MikeMessageParameters,
): params is ThermalLiDARResponseParameters {
  return "ProcessedHeatThermalData" in params || "LiDARData" in params;
}

export function isLaserScanParameters(
  params: MikeMessageParameters,
): params is LaserScanParameterModel {
  return "LiDARDataId" in params || "LiDARMeasurementsCloudFileKey" in params;
}

/**
 * Check if conversation ID is generic (default conversation)
 * @param conversationId - Conversation ID to check
 * @returns True if conversation ID is generic
 */
export function isConversationGeneric(conversationId?: string): boolean {
  return conversationId === GENERIC_CONVERSATION_ID;
}
